import { useQueries } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { endOfWeek, format, startOfWeek } from "date-fns"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { Minus, Plus } from "lucide-react"
import { useGetBackofficeClusterKeysQuery } from "modules/backoffice/hooks/clusters/useGetBackofficeClusterKeysQuery"
import { useUpdateClusterScoreMutation } from "modules/backoffice/hooks/clusters/useUpdateClusterScoreMutation"
import { useGetBackofficeCompaniesQuery } from "modules/backoffice/hooks/companies/useGetBackofficeCompaniesQuery"
import { backofficeCampaignsQuery } from "modules/backoffice/lib/campaigns/backofficeCampaignsQuery"
import { backofficeGoalsQuery } from "modules/backoffice/lib/campaigns/backofficeGoalsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeGetClustersKeysResponseDataSchema, OrganizationTypeEnum } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof BackofficeGetClustersKeysResponseDataSchema>[number]
>()

type ClustersQueryReturn = ReturnType<typeof useGetBackofficeClusterKeysQuery>

type Props = {
  clusters: ClustersQueryReturn["data"]
  isPending: ClustersQueryReturn["isPending"]
  searchQuery?: string
  selectedKeys?: string[]
  onSelectionChange?: (keys: string[]) => void
}

export const BackofficeClustersTable = ({
  clusters,
  isPending,
  searchQuery,
  selectedKeys,
  onSelectionChange,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync: updateScore } = useUpdateClusterScoreMutation()

  const { data: companies } = useGetBackofficeCompaniesQuery({
    query: {
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
      filterBy: {
        type: [OrganizationTypeEnum.Client, OrganizationTypeEnum.Partner],
      },
    },
  })

  const companyMap = useMemo(() => {
    if (!companies) return new Map<string, string>()
    return new Map(companies.map((company) => [company.id, company.name]))
  }, [companies])

  const filteredClusters = useMemo(() => {
    if (!clusters) return clusters
    let result = clusters

    if (searchQuery) {
      const queryLower = searchQuery.toLowerCase()
      result = result.filter((cluster) => {
        const companyName = companyMap.get(cluster.companyId)?.toLowerCase() ?? ""
        return companyName.includes(queryLower)
      })
    }

    // Sort by score (descending) to ensure proper order
    return result.sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
  }, [clusters, searchQuery, companyMap])

  const uniqueCompanyIds = useMemo(() => {
    if (!clusters) return []
    const companyIds = new Set(
      clusters
        .filter((cluster) => cluster.goalId || cluster.campaignId)
        .map((cluster) => cluster.companyId),
    )
    return Array.from(companyIds)
  }, [clusters])

  const goalsQueries = useQueries({
    queries: uniqueCompanyIds.map((companyId) =>
      backofficeGoalsQuery({
        companyId,
        query: {
          page: 1,
          total: 100,
          sortBy: { field: "name", order: "asc" },
        },
      }),
    ),
  })

  const goalMap = useMemo(() => {
    const map = new Map<string, string>()
    goalsQueries.forEach((query) => {
      if (query.data) {
        query.data.forEach((goal) => {
          map.set(goal.id, goal.name)
        })
      }
    })
    return map
  }, [goalsQueries])

  const campaignsQueries = useQueries({
    queries: uniqueCompanyIds.map((companyId) =>
      backofficeCampaignsQuery({
        companyId,
        query: {
          page: 1,
          total: 100,
          sortBy: { field: "name", order: "asc" },
        },
      }),
    ),
  })

  const campaignMap = useMemo(() => {
    const map = new Map<string, string>()
    campaignsQueries.forEach((query) => {
      if (query.data) {
        query.data.forEach((campaign) => {
          map.set(campaign.id, campaign.name ?? "")
        })
      }
    })
    return map
  }, [campaignsQueries])

  const columns = useMemo(() => {
    const baseColumns = columnHelper.columns([
      columnHelper.accessor("companyId", {
        header: t("backoffice.clusters.table.companyName"),
        cell: (props) => {
          const companyId = props.getValue()
          return companyMap.get(companyId) ?? companyId
        },
      }),
      columnHelper.accessor("domain", {
        header: t("backoffice.clusters.table.domain"),
      }),
      columnHelper.accessor("goalId", {
        header: t("backoffice.clusters.table.goalName"),
        cell: (props) => {
          const goalId = props.getValue()
          if (!goalId) return "-"
          return goalMap.get(goalId) ?? goalId
        },
      }),
      columnHelper.accessor("campaignId", {
        header: t("backoffice.clusters.table.campaignName"),
        cell: (props) => {
          const campaignId = props.getValue()
          if (!campaignId) return "-"
          return campaignMap.get(campaignId) ?? campaignId
        },
      }),
      columnHelper.display({
        id: "dateRange",
        header: t("backoffice.clusters.table.dateRange"),
        cell: (props) => {
          const from = props.row.original.from
          const weekStart = startOfWeek(from, { weekStartsOn: 1 })
          const weekEnd = endOfWeek(from, { weekStartsOn: 1 })
          return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`
        },
      }),
      columnHelper.accessor("epsilon", {
        header: t("backoffice.clusters.table.epsilon"),
        cell: (props) => props.getValue() ?? "-",
        meta: { className: "font-mono" },
      }),
      columnHelper.accessor("samplingFactor", {
        header: t("backoffice.clusters.table.samplingFactor"),
        cell: (props) => props.getValue() ?? "-",
        meta: { className: "font-mono" },
      }),
      columnHelper.accessor("selectionMethod", {
        header: t("backoffice.clusters.table.selectionMethod"),
        cell: (props) => props.getValue() ?? "-",
        meta: { className: "font-mono" },
      }),
      columnHelper.accessor("score", {
        header: t("backoffice.clusters.table.score"),
        cell: (props) => {
          const cluster = props.row.original
          const score = cluster.score ?? 0

          const handleIncrement = async (e: React.MouseEvent) => {
            e.stopPropagation()
            if (score < 3) {
              await updateScore({ key: cluster.key, score: score + 1 })
            }
          }

          const handleDecrement = async (e: React.MouseEvent) => {
            e.stopPropagation()
            if (score > 0) {
              await updateScore({ key: cluster.key, score: score - 1 })
            }
          }

          return (
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={handleDecrement}
                disabled={score === 0}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <span className="min-w-[1.5rem] text-center font-medium">{score}</span>
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={handleIncrement}
                disabled={score === 3}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )
        },
      }),
    ])

    if (onSelectionChange) {
      const totalRows = filteredClusters?.length ?? 0
      const selectedCount = selectedKeys?.length ?? 0
      const isAllSelected = totalRows > 0 && selectedCount === totalRows
      const isIndeterminate = selectedCount > 0 && selectedCount < totalRows
      let headerCheckboxState: boolean | "indeterminate" = false
      if (isAllSelected) {
        headerCheckboxState = true
      } else if (isIndeterminate) {
        headerCheckboxState = "indeterminate"
      }

      baseColumns.push(
        columnHelper.display({
          id: "select",
          header: () => (
            <Checkbox
              aria-label={t("backoffice.clusters.selectAll")}
              checked={headerCheckboxState}
              onCheckedChange={(checked) => {
                if (!onSelectionChange) return
                if (checked === true) {
                  onSelectionChange(filteredClusters?.map((cluster) => cluster.key) ?? [])
                  return
                }
                onSelectionChange([])
              }}
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              checked={selectedKeys?.includes(row.original.key) ?? false}
              onClick={(event) => event.stopPropagation()}
              onCheckedChange={(checked) => {
                if (!onSelectionChange) return
                if (checked === true) {
                  const next = new Set(selectedKeys ?? [])
                  next.add(row.original.key)
                  onSelectionChange(Array.from(next))
                  return
                }
                onSelectionChange((selectedKeys ?? []).filter((key) => key !== row.original.key))
              }}
            />
          ),
        }),
      )
    }

    return baseColumns
  }, [
    t,
    companyMap,
    goalMap,
    campaignMap,
    updateScore,
    onSelectionChange,
    selectedKeys,
    filteredClusters,
  ])

  const table = useTable({
    data: filteredClusters ?? [],
    columns,
    features,
  })

  function handleRowClick(key: string) {
    navigate({
      to: "/backoffice/clusters",
      search: (prev) => ({ ...prev, key }),
    })
  }

  function renderContent() {
    if (isPending || clusters === undefined) {
      return <TableSkeleton colSpans={columns.map(() => 1)} />
    }

    if (filteredClusters?.length === 0) {
      return (
        <TableEmptySlate colSpan={columns.length} message={t("backoffice.clusters.table.empty")} />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.key}
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => handleRowClick(row.original.key)}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            {...cell.column.columnDef.meta}
            onClick={(e) => {
              if (cell.column.id === "score" || cell.column.id === "select") {
                e.stopPropagation()
              }
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
